import { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Button, Box, Stack, CircularProgress, Link } from '@mui/material'
import { loginAccount } from '../actions'
import { ILogin, IReducer } from '../interface'
import { FormattedMessage, useIntl } from 'react-intl'
import { InputField } from '../components/Common/InputField'
import { useCookies } from 'react-cookie'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { OpenInNew } from '@mui/icons-material'
import SeoLogin from 'src/components/seo/SeoLogin'
import { FormProvider, useForm } from 'react-hook-form'
import { validateRequired } from 'src/validators/validate'
import { getUserLicensesGQL, loginWithCredsGQL } from 'src/services/graphql/schemas/authentication'
import { NotificationManager } from 'src/functions/notifications'
import { ALLOWED_FORUM_EMAILS, BASE_URL_GEMVISION, MG_APP_ID } from 'src/constants'
import { AuthenticationError } from 'src/utils/errors/authentication'

const Login = (props: any) => {
  const [cookies, setCookies] = useCookies(['country_code', 'temporary_user_id', 'popupDay'])
  const dispatch = useDispatch()
  const intl = useIntl()
  const navigate = useNavigate()
  const accountReducer = useSelector((state: IReducer) => state.accountReducer)
  const isAuthenticated = useSelector((state: IReducer) => state.accountReducer.isAuthenticated)
  const lastHistoryAvailable = useSelector((state: IReducer) => state.historyReducer!.lastHistoryAvailable)
  const [loginError, setLoginError] = useState(false)
  const [isGonnaRedirect, setIsGonnaRedirect] = useState(false)
  const [redirectUrl, setRedirectUrl] = useState('')
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const forumSso = searchParams.get('sso')
  const forumSig = searchParams.get('sig')

  useEffect(() => {
    try {
      const params = location.search.substring(1).split('=')
      if (params[0] === 'getBack') {
        setIsGonnaRedirect(true)
        setRedirectUrl(params[1])
      }
    } catch (error) {
      console.error(error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isAuthenticated) {
      if (isGonnaRedirect) {
        window.location.href = redirectUrl
      } else {
        navigate(lastHistoryAvailable)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.history, isAuthenticated, lastHistoryAvailable])

  const hasMGLicense = async (accessToken: string) => {
    // Check if user has MG License
    try {
      const { total: activeLicenses } = await getUserLicensesGQL(MG_APP_ID, accessToken)
      if (activeLicenses <= 0) {
        return false
      }
      return true
    } catch (error) {
      console.error('Error checking MG license -', error)
      return false
    }
  }

  const loginFromForum = async (values: ILogin) => {
    // Handle forum login
    const { userId, email, firstName, lastName, AccessToken } = await loginWithCredsGQL(
      values.username,
      values.password
    )
    const redirectUrl = `${BASE_URL_GEMVISION}/forum-sso?userId=${userId}&username=${values.username}&email=${email}&firstName=${firstName}&lastName=${lastName}&sso=${forumSso}&sig=${forumSig}`

    if (ALLOWED_FORUM_EMAILS.includes(values.username)) {
      window.location.href = redirectUrl
    } else {
      const haveLicense = await hasMGLicense(AccessToken)
      if (haveLicense) {
        window.location.href = redirectUrl
      } else {
        NotificationManager.error(<FormattedMessage id='notification.noActiveLicense' />)
      }
    }
  }

  const onSubmit = async (values: ILogin) => {
    try {
      if (forumSig && forumSso) {
        await loginFromForum(values)
        return
      }
      await dispatch(loginAccount(values, setLoginError, setCookies, cookies.popupDay))
    } catch (error) {
      handleLoginError(error)
      console.error('onSubmit - ', error)
    }
  }

  const handleLoginError = (error: unknown) => {
    if (error instanceof AuthenticationError) {
      NotificationManager.error(<FormattedMessage id={error.messageId} />)
    } else if (error instanceof Error) {
      NotificationManager.error(<FormattedMessage id='notification.UnexpectedError' />)
    } else {
      console.error('Unexpected error on loginAccount -', error)
    }
  }

  const methods = useForm({ mode: 'all', defaultValues: { username: '', password: '' } })

  const {
    handleSubmit,
    formState: { isSubmitting }
  } = methods

  return (
    <Stack
      sx={{
        minHeight: { xs: 'auto', sm: 'calc(100svh - 60px)' },
        width: '100%',
        p: { xs: 0, sm: 8 },
        alignItems: 'center'
      }}
    >
      <SeoLogin
        title={intl.formatMessage({ id: 'seo.login.title' })}
        description={intl.formatMessage({ id: 'seo.login.description' })}
      />
      <Box
        sx={{
          py: 6,
          m: 'auto',
          bgcolor: '#222',
          p: 4,
          width: '100%',
          maxWidth: { xs: 'auto', sm: 480 },
          borderRadius: { xs: 0, sm: 3 }
        }}
      >
        <Box
          component='img'
          sx={{ width: 128, mt: 3 }}
          src={'https://gemvision.com/Images/gemvision_logo_white.svg'}
          alt='Stuller'
        />
        <FormProvider {...methods}>
          <form
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              marginTop: '2rem',
              gap: '1rem'
            }}
            onSubmit={handleSubmit(onSubmit)}
          >
            <InputField
              name='username'
              type='text'
              title={intl.formatMessage({ id: 'account.username' })}
              disabled={accountReducer.loggingin || isSubmitting}
              rules={{ ...validateRequired(intl) }}
            />
            <InputField
              name='password'
              type='password'
              title={intl.formatMessage({ id: 'account.password' })}
              disabled={accountReducer.loggingin || isSubmitting}
              rules={{ ...validateRequired(intl) }}
            />
            {loginError && (
              <Button
                href='https://www.stuller.com/login'
                target='_blank'
                rel='noopener noreferrer'
                variant='contained'
                color='secondary'
                sx={{ lineHeight: 1.5, borderRadius: 2 }}
                endIcon={<OpenInNew />}
              >
                <FormattedMessage id='account.loginStullerFirst' />
              </Button>
            )}
            <Button
              fullWidth
              type='submit'
              variant='contained'
              color='primary'
              sx={{ textTransform: 'uppercase', minHeight: 60, borderRadius: 2, mt: 2 }}
              disabled={accountReducer.loggingin || isSubmitting}
              data-cy='login-button'
            >
              {isSubmitting ? <CircularProgress size={24} /> : <FormattedMessage id='account.logIn' />}
            </Button>
          </form>
        </FormProvider>
        <Stack direction='row' justifyContent='space-between' flexWrap='wrap' mt={4} gap={3}>
          <Link
            color='secondary'
            href='https://www.stuller.com/accountsignup/'
            target='_blank'
            rel='noopener noreferrer'
            title='Create'
          >
            <FormattedMessage id='account.createAccount' />
          </Link>
          <Link
            color='secondary'
            href='https://www.stuller.com/forgot'
            target='_blank'
            rel='noopener noreferrer'
            title='forgot'
          >
            <FormattedMessage id='account.forgotYourPassword_' />
          </Link>
        </Stack>
      </Box>
    </Stack>
  )
}

export default Login
